import { sanitizeCssColor } from "@/lib/security";
import {
  subscribeRealtimeSocket,
  type RealtimeConnectionState,
} from "@/lib/sharedRealtimeSocket";
import type { AuthenticatedWebSocketConfig } from "@/lib/websocket";
import type {
  CursorPosition,
  PresenceUser,
  ProjectWebSocketEvent,
  RowPresence,
} from "@/types/projectPresence";

interface Snapshot {
  onlineUsers: PresenceUser[];
  cursorPositions: Map<number, CursorPosition>;
  activeRows: Map<string, RowPresence>;
  connectionState: RealtimeConnectionState;
}
interface Listener {
  onStoreChange: () => void;
  onEvent: (payload: ProjectWebSocketEvent) => void;
}
interface Store {
  snapshot: Snapshot;
  listeners: Set<Listener>;
  transport: ReturnType<typeof subscribeRealtimeSocket> | null;
}

export const EMPTY_PROJECT_PRESENCE: Snapshot = {
  onlineUsers: [],
  cursorPositions: new Map(),
  activeRows: new Map(),
  connectionState: "disconnected",
};
const stores = new Map<string, Store>();
const identity = (config: AuthenticatedWebSocketConfig) =>
  JSON.stringify([config.url, config.protocols]);

function updatePresence(
  snapshot: Snapshot,
  payload: ProjectWebSocketEvent
): Snapshot {
  const kind = payload.type || payload.event;
  const user = payload.user as PresenceUser | undefined;
  if (kind === "presence.join" && user) {
    if (snapshot.onlineUsers.some((online) => online.id === user.id))
      return snapshot;
    return { ...snapshot, onlineUsers: [...snapshot.onlineUsers, user] };
  }
  if (kind === "presence.leave" && user) {
    const cursorPositions = new Map(snapshot.cursorPositions);
    cursorPositions.delete(user.id);
    const activeRows = new Map(
      [...snapshot.activeRows].filter(([, row]) => row.userId !== user.id)
    );
    return {
      ...snapshot,
      onlineUsers: snapshot.onlineUsers.filter(
        (online) => online.id !== user.id
      ),
      cursorPositions,
      activeRows,
    };
  }
  if (
    kind === "presence.cursor" &&
    user &&
    typeof payload.lat === "number" &&
    typeof payload.lng === "number"
  ) {
    const cursorPositions = new Map(snapshot.cursorPositions);
    cursorPositions.set(user.id, {
      user: { ...user, avatar_color: sanitizeCssColor(user.avatar_color) },
      lat: payload.lat,
      lng: payload.lng,
      receivedAt: Date.now(),
    });
    return { ...snapshot, cursorPositions };
  }
  if (
    kind === "presence.row" &&
    typeof payload.user_id === "number" &&
    typeof payload.layer_id === "number"
  ) {
    const activeRows = new Map(snapshot.activeRows);
    const key = `${payload.layer_id}:${payload.user_id}`;
    if (payload.feature_id == null) activeRows.delete(key);
    else {
      // The server keeps one active row per project/user, including when the
      // user moves to another layer. Do not retain that user's old claims.
      for (const [oldKey, row] of activeRows)
        if (row.userId === payload.user_id) activeRows.delete(oldKey);
      activeRows.set(key, {
        userId: payload.user_id,
        layerId: payload.layer_id,
        featureId: String(payload.feature_id),
        username: typeof payload.username === "string" ? payload.username : "",
        avatarColor: sanitizeCssColor(payload.avatar_color),
      });
    }
    return { ...snapshot, activeRows };
  }
  return snapshot;
}

export function getProjectPresenceSnapshot(
  config: AuthenticatedWebSocketConfig
): Snapshot {
  return stores.get(identity(config))?.snapshot ?? EMPTY_PROJECT_PRESENCE;
}

export function subscribeProjectPresence(
  config: AuthenticatedWebSocketConfig,
  listener: Listener
): () => void {
  const key = identity(config);
  let store = stores.get(key);
  if (!store) {
    store = {
      snapshot: EMPTY_PROJECT_PRESENCE,
      listeners: new Set(),
      transport: null,
    };
    stores.set(key, store);
  }
  const current = store;
  current.listeners.add(listener);
  const publish = (snapshot: Snapshot) => {
    if (snapshot === current.snapshot) return;
    current.snapshot = snapshot;
    for (const subscriber of current.listeners) subscriber.onStoreChange();
  };
  if (!current.transport)
    current.transport = subscribeRealtimeSocket(config, {
      onState: (connectionState) => {
        // A disconnected stream cannot claim its old presence remains current.
        publish({
          ...(connectionState === "connected"
            ? current.snapshot
            : EMPTY_PROJECT_PRESENCE),
          connectionState,
        });
      },
      onMessage: (event) => {
        let payload: ProjectWebSocketEvent;
        try {
          payload = JSON.parse(event.data) as ProjectWebSocketEvent;
        } catch {
          return;
        }
        if (!payload || typeof payload !== "object") return;
        publish(updatePresence(current.snapshot, payload));
        for (const subscriber of current.listeners) {
          try {
            subscriber.onEvent(payload);
          } catch (error) {
            console.error("Project event handler failed", error);
          }
        }
      },
    });
  let released = false;
  return () => {
    if (released) return;
    released = true;
    current.listeners.delete(listener);
    queueMicrotask(() => {
      if (current.listeners.size || stores.get(key) !== current) return;
      stores.delete(key);
      current.transport?.release();
      current.transport = null;
    });
  };
}

export function reconnectProjectPresence(
  config: AuthenticatedWebSocketConfig
): void {
  const store = stores.get(identity(config));
  if (store?.listeners.size) store.transport?.reconnect();
}

export function sendProjectPresence(
  config: AuthenticatedWebSocketConfig,
  payload: ProjectWebSocketEvent
): void {
  const store = stores.get(identity(config));
  if (store?.listeners.size) store.transport?.send(JSON.stringify(payload));
}
